'use client';

import { TargetIcon, CaretRightIcon } from '@phosphor-icons/react';
import { useFormatCurrency } from '@/hooks/useFormatCurrency';
import type { GoalSummary } from '@/types';
import {
  formatYmShort,
  getGoalIcon,
  goalStatus,
  monthsUntilDeadline,
  projectedMonthsToTarget,
  runtimeStatusColor,
  runtimeStatusLabel,
} from './helpers';
import { BalanceMicroSparkline } from './BalanceMicroSparkline';

interface Props {
  goal: GoalSummary;
  onEdit: () => void;
}

export function GoalCard({ goal, onEdit }: Props) {
  const formatCurrency = useFormatCurrency();
  const Icon = getGoalIcon(goal.icon, goal.type);
  const isDebt = goal.type === 'DebtPayoff';
  const target = goal.targetAmount;
  const saved = goal.currentAmount;
  const p = target > 0 ? Math.min(999, Math.round((saved / target) * 100)) : 0;
  const status = goalStatus(goal);
  const statusColor = runtimeStatusColor(status);
  const statusLabel = runtimeStatusLabel(status);
  const monthsLeft = monthsUntilDeadline(goal.targetDate);
  const monthsNeeded = projectedMonthsToTarget(goal);
  const isRascunho = !goal.monthlyContribution;

  return (
    <button
      onClick={onEdit}
      type="button"
      className="w-full cursor-pointer rounded-[var(--radius-3xl)] border border-border bg-surface shadow-[var(--shadow-1)] p-4 text-left transition-colors hover:border-border-strong focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary motion-reduce:transition-none"
    >
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2.5">
          <div
            className={`w-9 h-9 rounded-md grid place-items-center shrink-0 ${isDebt ? 'bg-error-surface text-error-text' : 'bg-success-surface text-success-text'}`}
          >
            <Icon size={16} weight="bold" />
          </div>
          <div className="min-w-0">
            <div className="truncate text-base leading-tight font-semibold text-text-primary">{goal.name}</div>
            <div className="mt-0.5 text-2xs text-text-tertiary">
              {isDebt
                ? `Saldo devedor: ${formatCurrency(goal.currentBalance ?? 0)}`
                : `Meta: ${formatCurrency(target)}${goal.targetDate ? ` · ${formatYmShort(goal.targetDate.slice(0, 7))}` : ' · sem prazo'}`}
            </div>
          </div>
        </div>
        <div className="shrink-0 text-right">
          <div className={`font-display font-mono tabular-nums tracking-tight leading-none text-[1rem] sm:text-[1.125rem] font-bold ${isDebt ? 'text-error-text' : 'text-success-text'}`}>
            {formatCurrency(saved)}
          </div>
          <div className="mt-1 font-mono text-2xs text-text-tertiary">
            {isDebt ? 'pagos' : `de ${formatCurrency(target)}`}
          </div>
          {!isDebt && (
            <div className="mt-1.5 flex justify-end">
              <BalanceMicroSparkline goalId={goal.id} />
            </div>
          )}
        </div>
      </div>

      <div className="relative h-1.5 w-full overflow-hidden rounded-full bg-divider">
        <div
          className="h-full rounded-full"
          style={{ width: `${Math.min(100, p)}%`, backgroundColor: statusColor }}
        />
      </div>

      <div className="mt-2 flex items-center justify-between text-2xs">
        <div className="flex items-center gap-1.5">
          <span className="inline-block size-1.5 rounded-full" style={{ backgroundColor: statusColor }} />
          <span style={{ color: statusColor }} className="font-medium">{statusLabel}</span>
          <span className="font-mono text-text-tertiary tabular-nums">· {p}%</span>
        </div>
        {!isRascunho && (
          <span className="font-mono text-text-secondary tabular-nums">
            {isDebt
              ? `${formatCurrency(goal.currentBalance ?? 0)} restantes`
              : `${formatCurrency(Math.max(0, target - saved))} a guardar`}
          </span>
        )}
      </div>

      <div className="mt-3 flex items-center justify-between gap-3 border-t border-divider pt-3 text-2xs">
        <div className="flex items-center gap-1.5 text-text-secondary">
          <TargetIcon size={10} weight="bold" />
          {isDebt ? (
            <span>
              Quitada em {monthsNeeded === Infinity ? '—' : `${monthsNeeded} ${monthsNeeded === 1 ? 'mês' : 'meses'}`}
            </span>
          ) : monthsNeeded === Infinity ? (
            <span>Sem aportes — ajuste a contribuição</span>
          ) : (
            <span>
              {monthsNeeded} {monthsNeeded === 1 ? 'mês' : 'meses'} no ritmo atual
              {monthsLeft !== null && goal.targetDate && (
                <span className="text-text-tertiary"> · prazo {monthsLeft} {monthsLeft === 1 ? 'mês' : 'meses'}</span>
              )}
            </span>
          )}
        </div>
        <CaretRightIcon size={12} weight="bold" className="text-text-tertiary" />
      </div>
    </button>
  );
}
